import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ConjItem } from "@/lib/types";
import { PERSON_LABEL, TENSE_LABEL } from "@/lib/types";
import { useProfileStore } from "@/state/profile";
import { useSessionStore } from "@/state/session";
import { GameShell } from "@/components/GameShell";
import { FeedbackOverlay } from "@/components/FeedbackOverlay";
import { burstConfetti, sfxCorrect, sfxWrong } from "@/lib/rewards";
import { gradeLocally } from "@/lib/grade";
import { VerbTimeline } from "@/components/VerbTimeline";

export function ConjGame() {
  return (
    <>
      <GameShell game="conj">
        {({ item, disabled }) => {
          if (item.kind !== "conj") return null;
          return <ConjCard item={item} disabled={disabled} />;
        }}
      </GameShell>
      <ConjFeedback />
    </>
  );
}

function ConjCard({ item, disabled }: { item: ConjItem; disabled: boolean }) {
  const profileId = useProfileStore((s) => s.activeId);
  const answer = useSessionStore((s) => s.answer);
  const loaded = useSessionStore((s) => s.loaded);
  const support = loaded?.session.support_level ?? "standard";
  const [submitting, setSubmitting] = useState(false);
  const [guidePreference, setGuidePreference] = useState<
    Record<string, boolean>
  >({});
  const firstQuestionForVerb = !loaded?.session.items
    .slice(0, loaded.idx)
    .some(
      (previous) =>
        previous.kind === "conj" && previous.item_id === item.item_id,
    );
  const guideOpen = guidePreference[item.id] ??
    (support === "guided" || (support === "standard" && firstQuestionForVerb));

  async function pickOption(option: string) {
    if (disabled || submitting) return;
    setSubmitting(true);
    const { correct } = gradeLocally(item, option);
    if (correct) {
      sfxCorrect((useSessionStore.getState().loaded?.combo ?? 0) + 1);
      burstConfetti("#7B61FF");
    } else {
      sfxWrong();
    }
    await answer(profileId, option);
    setSubmitting(false);
  }

  const parts = item.sentence.split(/_{2,}/);

  return (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-full flex flex-col items-center justify-start pt-2"
    >
      <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
        <span className="pill bg-brand-violet/10 border-brand-violet/30 text-brand-violet">
          {TENSE_LABEL[item.tense]}
        </span>
        <span className="pill bg-brand-mint/15 border-brand-mint/40 text-brand-ink">
          {PERSON_LABEL[item.person]}
        </span>
        <span className="pill bg-brand-saffron/15 border-brand-saffron/40 text-brand-ink">
          {item.infinitive}
        </span>
      </div>

      <div className="w-full max-w-lg mb-3">
        <button
          type="button"
          onClick={() =>
            setGuidePreference((preferences) => ({
              ...preferences,
              [item.id]: !guideOpen,
            }))
          }
          aria-expanded={guideOpen}
          className="w-full rounded-2xl border border-brand-border bg-brand-surface/80 px-4 py-2.5 flex items-center justify-between gap-3 text-sm font-extrabold text-brand-ink"
        >
          <span>Tabla de {item.infinitive}</span>
          <span className="text-brand-violet">
            {guideOpen ? "Ocultar ↑" : "Ver guía ↓"}
          </span>
        </button>
        <AnimatePresence initial={false}>
          {guideOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-2">
                <VerbTimeline
                  timeline={item.timeline}
                  gerund={item.gerund}
                  currentTense={item.tense}
                  compact
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.div
        key={item.sentence}
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="chunk px-5 py-6 w-full max-w-lg text-center"
      >
        <div className="text-xs text-brand-muted uppercase tracking-widest font-black mb-3">
          Elige la forma de yo
        </div>
        <p className="font-display text-2xl leading-snug">
          {parts[0]}
          <span className="mx-1 inline-block min-w-[3em] border-b-4 border-dashed border-brand-coral text-brand-coral">
            ____
          </span>
          {parts.slice(1).join("")}
        </p>
        <div className="mt-3 text-sm text-brand-muted font-bold">
          → <em>{item.english}</em>
        </div>
      </motion.div>

      <div className="w-full max-w-lg mt-4 grid grid-cols-2 gap-3">
        {item.options.map((opt) => (
          <button
            key={opt}
            type="button"
            disabled={disabled || submitting}
            onClick={() => pickOption(opt)}
            className="min-h-16 text-lg sm:text-xl font-display px-3 py-3 rounded-chunk bg-brand-surface border-2 border-brand-border hover:border-brand-violet active:scale-95 transition-all shadow-chunk disabled:opacity-50 break-words"
          >
            {opt}
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function ConjFeedback() {
  const loaded = useSessionStore((s) => s.loaded);
  const advance = useSessionStore((s) => s.advance);
  return <FeedbackOverlay result={loaded?.lastResult ?? null} onNext={advance} />;
}
